import React from "react";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import { capitalizeAllLetters, textTruncate } from "Utils/utils";

const OPEN_SLOT_TEXT_CLASS =
  "font-semibold text-yellow-700 animate-open-slot-soft";

const SlotOpenText = ({ openSlotCount }) => (
  <span className={OPEN_SLOT_TEXT_CLASS}>
    {openSlotCount} slot{openSlotCount === 1 ? "" : "s"} open
  </span>
);

const getPersonLabel = (person) =>
  person?.name ||
  `${capitalizeAllLetters(person?.first_name || "")} ${capitalizeAllLetters(
    person?.last_name || ""
  )}`.trim();

const OpenSlotsCountLabel = ({ workerCount, openSlotCount }) => {
  const slotLabel = `${openSlotCount} slot${openSlotCount === 1 ? "" : "s"} open`;

  if (workerCount === 0) {
    return <SlotOpenText openSlotCount={openSlotCount} />;
  }

  const workerLabel = `${workerCount} worker${workerCount === 1 ? "" : "s"}`;
  return (
    <span className="capitalize text-gray-500">
      {workerLabel} + <SlotOpenText openSlotCount={openSlotCount} />
    </span>
  );
};

const SinglePersonWithOpenSlots = ({ person, openSlotCount, type }) => {
  const label = getPersonLabel(person);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 min-w-0">
        <AvatarGroup
          type={type}
          arrayData={[
            {
              ...person,
              first_name: capitalizeAllLetters(person?.first_name || ""),
              last_name: capitalizeAllLetters(person?.last_name || ""),
            },
          ]}
          cssClass="mt-0"
          maxLength={1}
          isNoRing
        />
        <span title={label} className="capitalize text-gray-500 text-sm truncate">
          {textTruncate(label, 20, 19)}
        </span>
      </div>
      <span className="text-[10px] pl-8">
        + <SlotOpenText openSlotCount={openSlotCount} />
      </span>
    </div>
  );
};

const PublishShiftPersonCell = ({
  people = [],
  showAvatars = false,
  emptyLabel = null,
  openSlotCount = 0,
  type = "worker",
}) => {
  const isWorkerCell = type === "worker";
  const hasOpenSlots = isWorkerCell && openSlotCount > 0;

  if (hasOpenSlots && people.length === 0) {
    return <SlotOpenText openSlotCount={openSlotCount} />;
  }

  if (emptyLabel && people.length === 0) {
    return <span className="text-red-300">{emptyLabel}</span>;
  }

  if (hasOpenSlots && people.length === 1) {
    return (
      <SinglePersonWithOpenSlots
        person={people[0]}
        openSlotCount={openSlotCount}
        type={type}
      />
    );
  }

  if (showAvatars && people.length > 1) {
    return (
      <div className="flex flex-col gap-1">
        <AvatarGroup
          type={type}
          arrayData={people.map((person) => ({
            ...person,
            first_name: capitalizeAllLetters(person?.first_name || ""),
            last_name: capitalizeAllLetters(person?.last_name || ""),
          }))}
          cssClass="mt-0"
          maxLength={4}
          isNoRing
        />
        {hasOpenSlots ? (
          <span className="text-[10px]">
            <OpenSlotsCountLabel
              workerCount={people.length}
              openSlotCount={openSlotCount}
            />
          </span>
        ) : (
          <span className="text-[10px] text-gray-500">
            {people.length} {type === "worker" ? "workers" : "participants"}
          </span>
        )}
      </div>
    );
  }

  const label = getPersonLabel(people[0]);

  return (
    <span title={label} className="capitalize text-gray-500">
      {textTruncate(label, 24, 23)}
    </span>
  );
};

export default PublishShiftPersonCell;
